import { buildCreatorPublicUrl, CreatorUrlType } from "./buildCreatorPublicUrl";
import { getCreatorSlugFromHost } from "./getCreatorSlugFromHost";
import { isVercelDeploymentHost } from "./creatorHost";

export type CreatorBrowsableUrlPrefs = {
  urlType: CreatorUrlType | null;
  domain: string | null;
};

type MaybeUserRow = {
  username?: string;
  domain?: string | null;
  url_type?: CreatorUrlType | null;
};

function stripProtocol(url: string): string {
  // Be tolerant of malformed schemes like "https//example.com" (missing ':')
  return String(url || "").replace(/^https?:?\/\//, "");
}

function normalizeCustomDomain(customDomain: string): string {
  const raw = String(customDomain || "").trim();
  if (!raw) return "";
  // Accept "example.com" or "https://example.com"
  const noProto = stripProtocol(raw).replace(/\/+$/, "");
  return noProto;
}

/** Public links should use https on Browsable-owned hosts even if the dashboard is opened over http. */
function preferHttpsOnBrowsableHosts(url: string): string {
  const s = String(url || "").trim();
  if (!s) return s;
  try {
    const u = new URL(s);
    const h = u.hostname.toLowerCase();
    const isBrowsableOwned =
      h === "browsable.ai" ||
      h === "www.browsable.ai" ||
      h === "browsable.bio" ||
      h === "www.browsable.bio" ||
      h.endsWith(".browsable.ai") ||
      h.endsWith(".browsable.bio") ||
      h.includes("searchbubble.com");
    if (isBrowsableOwned && u.protocol === "http:") {
      u.protocol = "https:";
      return u.toString().replace(/\/+$/, "");
    }
  } catch (_e) {
    // ignore
  }
  return s.replace(/\/+$/, "");
}

/**
 * On Vercel deploys only: rewrite absolute off-host URLs back to a same-origin
 * path so preview testing never leaves *.vercel.app.
 * No-op on browsable.ai / browsable.bio / custom domains / localhost.
 */
export function keepNavigationOnVercelDeploy(destination: string): string {
  const trimmed = String(destination || "").trim();
  if (!trimmed || typeof window === "undefined") return trimmed;
  if (!isVercelDeploymentHost()) return trimmed;

  try {
    if (!/^https?:\/\//i.test(trimmed)) return trimmed;
    const target = new URL(trimmed);
    if (isVercelDeploymentHost(target.hostname)) return trimmed;
    return `${target.pathname}${target.search}${target.hash}` || "/";
  } catch (_e) {
    return trimmed;
  }
}

/** True when the current path is this creator's public page on a platform apex host (path-style URLs). */
export function isViewingCreatorOnPlatformPath(
  username: string,
  pathname?: string,
): boolean {
  const user = String(username || "").trim();
  if (!user || typeof window === "undefined") return false;

  const p =
    String(pathname ?? window.location.pathname)
      .split("?")[0]
      .replace(/\/$/, "") || "/";
  const lower = user.toLowerCase();
  const enc = encodeURIComponent(user);

  const roots = [enc, user, lower];
  for (const root of roots) {
    for (const prefix of ["", "/embed", "/preview"]) {
      const base = `${prefix}/${root}`;
      if (p === base || p.startsWith(`${base}/`)) return true;
    }
  }
  return false;
}

/**
 * Priority:
 * 1) custom domain (e.g. creator.com)
 * 2) saved preference (domain + url_type) from loaded dashboard user row (`browsableUrlPrefs`), when provided
 * 3) saved preference from localStorage "member" when its username matches `username`
 * 4) default legacy: browsable.ai/{username}
 *
 * Returns a URL origin+path base WITHOUT trailing slash (except when empty).
 */
export function getCreatorPublicBaseUrl(args: {
  username: string;
  customDomain?: string | null | undefined;
  /**
   * Loaded `user` row for this dashboard creator (url_type + domain). When set, overrides
   * localStorage `member` for Browsable URL shape — fixes admin/impersonation where `member`
   * is a different account than `username`.
   */
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null | undefined;
}): string {
  const username = String(args.username || "").trim();
  let result = "";
  if (!username) return "";

  // Vercel preview/prod: always stay on this deploy. Never emit browsable.ai/bio
  // or custom-domain absolute URLs (those feel like "redirects" when clicked).
  try {
    if (isVercelDeploymentHost()) {
      return `${window.location.origin.replace(/\/+$/, "")}/${encodeURIComponent(
        username,
      )}`;
    }
  } catch (_e) {
    // ignore
  }

  const cd = normalizeCustomDomain(String(args.customDomain || ""));
  if (cd) {
    result = `https://${cd}`;
    return preferHttpsOnBrowsableHosts(result);
  }

  try {
    const host = String(window.location.hostname || "").toLowerCase();
    // Any Browsable creator subdomain or dev {slug}.localhost — use the host the visitor is on.
    if (getCreatorSlugFromHost(host)) {
      result = window.location.origin.replace(/\/+$/, "");
      return preferHttpsOnBrowsableHosts(result);
    }
  } catch (_e) {
    // ignore
  }

  try {
    // Skip the "use bare origin" shortcut when on the preview route — the preview page
    // is a dashboard feature, not the creator's actual public page.  Returning only
    // window.location.origin here strips the username from the URL, which causes the
    // share-button on the preview header to copy "browsable.ai" instead of
    // "browsable.ai/{username}".  Fall through to browsableUrlPrefs / buildCreatorPublicUrl
    // so the username is always included in the share URL.
    const isPreviewPath = String(
      typeof window !== "undefined" ? window.location.pathname : "",
    ).startsWith("/preview/");
    if (!isPreviewPath && isViewingCreatorOnPlatformPath(username)) {
      result = window.location.origin.replace(/\/+$/, "");
      return preferHttpsOnBrowsableHosts(result);
    }
  } catch (_e) {
    // ignore
  }

  // If the viewer is already on a non-platform host, treat it as the custom domain.
  // This covers public pages (not logged in) where `useCustomDomain()` may be null.
  try {
    const host = String(window.location.hostname || "").toLowerCase();
    const isLocalhost =
      host === "localhost" || host === "127.0.0.1" || host.endsWith(".localhost");
    const isPlatformHost =
      host === "browsable.ai" ||
      host === "www.browsable.ai" ||
      host === "browsable.bio" ||
      host === "www.browsable.bio" ||
      host.endsWith(".browsable.ai") ||
      host.endsWith(".browsable.bio") ||
      host.includes("searchbubble.com") ||
      host.endsWith(".vercel.app");
    if (!isLocalhost && !isPlatformHost) {
      result = window.location.origin.replace(/\/+$/, "");
      return preferHttpsOnBrowsableHosts(result);
    }
  } catch (_e) {
    // ignore
  }

  // If we're already on the creator's subdomain (public view), use current origin.
  // This avoids generating `https://www.browsable.ai/{username}` when the viewer
  // is already on `https://{username}.browsable.ai`.
  try {
    const host = String(window.location.hostname || "").toLowerCase();
    const expectedLabel = username.toLowerCase();
    const hostMatchesCreator =
      host === `${expectedLabel}.localhost` ||
      (host.endsWith(".localhost") && host.startsWith(`${expectedLabel}.`)) ||
      [".browsable.ai", ".browsable.bio"].some(
        (suffix) => host === `${expectedLabel}${suffix}`,
      );
    if (hostMatchesCreator) {
      result = window.location.origin.replace(/\/+$/, "");
      return preferHttpsOnBrowsableHosts(result);
    }
  } catch (_e) {
    // ignore
  }

  const prefs = args.browsableUrlPrefs;
  if (prefs) {
    result = buildCreatorPublicUrl({
      username,
      urlType: prefs.urlType ?? null,
      domain: prefs.domain ?? null,
    }).replace(/\/+$/, "");
    return preferHttpsOnBrowsableHosts(result);
  }

  // Prefer localStorage "member" (set on login) to avoid extra network calls.
  try {
    const raw = localStorage.getItem("member");
    if (raw) {
      const parsed = JSON.parse(raw) as MaybeUserRow;
      if (parsed && String(parsed.username || "").trim() === username) {
        result = buildCreatorPublicUrl({
          username,
          urlType: parsed.url_type ?? null,
          domain: parsed.domain ?? null,
        }).replace(/\/+$/, "");
        return preferHttpsOnBrowsableHosts(result);
      }
    }
  } catch (_e) {
    // ignore
  }

  result = buildCreatorPublicUrl({
    username,
    urlType: null,
    domain: null,
  }).replace(/\/+$/, "");
  return preferHttpsOnBrowsableHosts(result);
}

/** Public collection / carousel "View All" URL (creator base + route segment). */
export function buildCreatorCollectionUrl(args: {
  username: string;
  route?: string | null;
  customDomain?: string | null | undefined;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null | undefined;
}): string {
  const username = String(args.username || "").trim();
  if (!username) return "";

  const route = String(args.route || "").replace(/^\/+/, "").replace(/\/+$/, "");
  const base = getCreatorPublicBaseUrl({
    username,
    customDomain: args.customDomain,
    browsableUrlPrefs: args.browsableUrlPrefs,
  }).replace(/\/+$/, "");

  if (!base) return "";
  return route ? `${base}/${route}` : base;
}
